import { useEffect, useState } from "react";
import { useLazyCheckSlugQuery } from "@/src/onboarding/services/onboarding-http.service";

const SLUG_REGEX = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
const DEBOUNCE_MS = 500;

interface UseSlugCheckResult {
	isAvailable: boolean;
	isUnavailable: boolean;
	isChecking: boolean;
	isValid: boolean;
	isModified: boolean;
}

export function useSlugCheck(slug: string, currentSlug?: string): UseSlugCheckResult {
	const [trigger, { data, isFetching }] = useLazyCheckSlugQuery();
	const [debouncedSlug, setDebouncedSlug] = useState(slug);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSlug(slug), DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [slug]);

	useEffect(() => {
		if (currentSlug !== undefined && debouncedSlug === currentSlug) return;
		if (debouncedSlug.length >= 3 && SLUG_REGEX.test(debouncedSlug)) {
			trigger(debouncedSlug);
		}
	}, [debouncedSlug, currentSlug, trigger]);

	const isValid = slug.length >= 3 && SLUG_REGEX.test(slug);
	const isModified = currentSlug === undefined ? true : slug !== currentSlug;
	const isChecking = isModified && (isFetching || slug !== debouncedSlug);
	const isAvailable = isModified && isValid && !isChecking && data?.available === true;
	const isUnavailable = isModified && isValid && !isChecking && data?.available === false;

	return { isAvailable, isUnavailable, isChecking, isValid, isModified };
}
